<script setup>
import Decimal from 'decimal.js-light'
import { nextTick } from 'vue'
import { useFutureStore } from '~/store/future'
import { onlyAllowNumber } from '@/utils/tools'
import { usePanelStore } from '~/store/panel'
import { add, div, fix, sub } from '~/utils/math'
import { usePanel } from '~/hooks/panel'
import { useUserStore } from '~/store/user'
import { useBonusStore } from '~/store/bonus'
import { wallet } from '~/apis/socket'
import { useBackUrl } from '~/hooks/index.js'

import XSlider from '~/components/common/xSlider.vue'
import xOptInput from '~/components/common/xOptInput.vue'
import xSelect from '~/components/common/xSelect.vue'
import ArrowIcon from '~/assets/images/common/solid-triangle.svg'
import OrderInfer from './OrderInfer.vue'
import TpSl from './TpSl.vue'
import Transfer from '../icons/Transfer.vue'
import Calculator from '../icons/Calculator.vue'

const { t, locale } = useI18n()
const localPath = useLocalePath()
const message = useMessage()
const router = useRouter()
const route = useRoute()
const { unitName, unitText, placeOrder } = usePanel()
const panel = usePanelStore()
const future = useFutureStore()
const tpsl = ref()
const user = useUserStore()
const bonus = useBonusStore()

const amountInput = ref()

const marks = {
  0: '0%',
  25: '25%',
  50: '50%',
  75: '75%',
  100: '100%',
}

const errMsg = ref({
  price: '',
  amount: '',
  triggerPrice: '',
})

if (panel.isLimit && future.overview.c) {
  panel.price = `${future.overview.c}`
}

// 计划委托 触发价格选项
const stopTriggerPriceOpts = computed(() => [
  {
    label: t('最新价'),
    key: 'LAST',
  },
  {
    label: t('标记价'),
    key: 'MARK',
  },
])

// 计划委托 执行价格选项
const stopExecutionPriceOpts = computed(() => [
  {
    label: t('限价'),
    key: 'LIMIT',
  },
  {
    label: t('市价'),
    key: 'MARKET',
  },
])

// 最小下单
const minOrder = computed(() => {
  let amount = 1
  let text = 1
  const { ps, size, base } = future
  if (future.isLotBase) {
    text = size
    amount = size
  }
  else {
    const max = Math.max(panel.getPriceBySide({ side: 1 }), panel.getPriceBySide({ side: -1 }))
    const oneLotCost = future.getOneLotCost({ price: max })
    amount = future.isLotQuote ? Decimal(oneLotCost).mul(future.positionConfig.leverage).toFixed(2, Decimal.ROUND_CEIL) : oneLotCost.toFixed(2, Decimal.ROUND_CEIL)
    text = fix(amount, ps)
  }
  const baseText = future.isLotBase || future.isLotCost ? '' : `≈ ${size} ${base}`
  return {
    amount,
    text: t('最小下单数量{0}', [`${text} ${unitName.value} ${baseText}`]),
  }
})

watch([() => panel.pe, () => panel.type, () => future.accountType], () => {
  resetErrMsg()
})

function onStep(side) {
  const { ps } = future.curInfo
  const step = div(1, 10 ** ps)
  const price = panel.price || future.getLastPrice()
  panel.price = String(side === 1 ? add(price, step) : sub(price, step))
}

function validate(side) {
  if (panel.isTrigger && !panel.stop.triggerPrice) {
    errMsg.value.triggerPrice = t('请输入{0}', [t('触发价格')])
    return false
  }

  if (!panel.price && !panel.isOrderMarket) {
    errMsg.value.price = t('请输入{0}', [panel.isTrigger ? t('执行价格') : t('价格')])
    return false
  }

  if (!panel.amount && !panel.sliderValue) {
    errMsg.value.amount = t('请输入{0}', [t('数量')])
    return false
  }

  if (!+panel.getPanelLots(side)) {
    errMsg.value.amount = minOrder.value.text
    return false
  }

  // 可用余额校验
  if (new Decimal(future.getMarginBalanceByCode(future.baseM)).lt(mul(panel.getPanelLots(side), future.getOneLotCost({ price: panel.getPriceBySide({ side }) })))) {
    message.error(t('可用余额不足'))
    return false
  }

  if (toValue(tpsl.value.check) && toValue(tpsl.value.isAdvance) && toValue(tpsl.value.side) !== side) {
    message.warning(t('目前下单方向与高级止盈止损设置方向不一致'))
    return false
  }

  if (!tpsl.value.validate(side)) {
    return false
  }

  resetErrMsg()

  return true
}

function reset() {
  panel.price = `${future.overview.c || ''}`
  panel.sliderValue = future.isGrants ? 100 : 0
  panel.amount = ''
  panel.stop.triggerPrice = ''
  bonus.grantsInfo = {}
  tpsl.value.reset()
  if (future.isGrants) {
    bonus.getaGrantsList()
    setTimeout(() => {
      bonus.getBounsUsedInfo()
    }, 1000)
    wallet.value.grants = {}
  }
}

async function place(side) {
  if (!user.isLogin) {
    router.push({
      path: `/${locale.value}/log-in`,
      query: { ...route.query, backUrl: encodeURIComponent(location.href) },
    })
  }
  const orderConditions = tpsl.value.getOrderConditions(side)

  if (!validate(side))
    return

  const success = await placeOrder({
    price: panel.isOrderMarket ? '' : panel.price,
    quantity: panel.getPanelLots(side),
    side,
    orderConfirmType: panel.type,
    isTrigger: panel.isTrigger,
    isTpsl: orderConditions.length > 0,
    orderConditions,
    bonusId: bonus.grantsInfo.id,
    reduceOnly: panel.pe === 2,
    stopType: panel.isTrigger ? 0 : toValue(tpsl.value.check) && orderConditions.length ? 2 : undefined,
  })
  success && reset()
}

async function onAmount() {
  if (panel.sliderValue) {
    panel.sliderValue = 0
    await nextTick()
    amountInput.value?.nInput?.focus()
  }
}

function onAdvance() {
  validate()
  return Object.values(errMsg.value).every(value => !value)
}

function resetErrMsg() {
  errMsg.value.amount = ''
  errMsg.value.price = ''
  errMsg.value.triggerPrice = ''
}

function amountAllowInput(value) {
  const dp = future.isLotQuote || future.isLotCost ? future.ps : (future.isLotBase ? future.as : 0)
  return onlyAllowNumber(value, dp)
}

const accountList = [
  { label: t('本金'), value: '0 USDT' },
  { label: t('体验金'), value: '1000 USDT' },
  { label: t('赠金') },
]
</script>

<template>
  <div>
    <div id="driverFuturesGrantsAccount">
      <div class="flex items-center space-x-2 rtl:space-x-reverse">
        <!-- <div class="flex-1 text-xs font-medium text-textSecondary">
          <FuturesPanelAccountSelector />
        </div> -->
        <div class="flex-1 text-xs font-medium">
          {{ $t('可用') }} {{ fix(future.getMarginBalanceByCode(future.baseM), 2) }}
        </div>
        <nuxt-link :to="localPath('/assets/transfer')">
          <Transfer class="cursor-pointer hover:text-primary" />
        </nuxt-link>
        <Calculator class="cursor-pointer hover:text-primary" @click="future.modal.calculator = true" />
      </div>
    </div>
    <n-tooltip v-if="panel.type === 'LIMIT'" placement="top-end" trigger="focus">
      <template #default>
        <div class="text-xs text-textPrimary">
          <p>{{ $t('最高买价') }} <span class="text-buy">{{ fix(future.bbo.b1, future.ps) ?? '-' }}</span></p>
          <p class="mt-1">
            {{ $t('最低卖价') }} <span class="text-sell">{{ fix(future.bbo.a1, future.ps) ?? '-' }}</span>
          </p>
        </div>
      </template>
      <template #trigger>
        <xOptInput v-model:value="panel.price" v-model:error="errMsg.price" placeholder="" :allow-input="(value) => onlyAllowNumber(value, future.ps)" class="mt-2" @handle-opt="onStep" @focus="errMsg.price = ''">
          <template #prefix>
            <span class="text-xs font-medium text-textTertiary">{{ $t('价格') }}</span>
          </template>
          <template #suffix-extra>
            <div class="cursor-pointer pe-2 text-primary" @click="panel.price = `${future.overview.c || ''}`">
              {{ $t('最新') }}
            </div>
          </template>
        </xOptInput>
      </template>
    </n-tooltip>

    <div v-else-if="panel.type === 'MARKET'" class="flex items-center h-10 px-3 mt-2 text-xs font-medium rounded-lg cursor-not-allowed bg-inputHovered text-textTertiary">
      {{ $t('市价') }}
    </div>
    <div v-else-if="panel.type === 'TRIGGER'">
      <xOptInput v-model:value="panel.stop.triggerPrice" v-model:error="errMsg.triggerPrice" :is-button="false" :allow-input="(value) => onlyAllowNumber(value, future.ps)" placeholder="" class="mt-2 [&_.n-input-wrapper]:!overflow-visible" @focus="errMsg.triggerPrice = ''">
        <template #prefix>
          <span class="text-xs font-medium text-textTertiary">{{ $t('触发价格') }}</span>
        </template>
        <template #suffix>
          <x-select v-model="panel.stop.triggerPriceType" :list="stopTriggerPriceOpts" current-class="text-textPrimary" />
        </template>
      </xOptInput>
      <div class="flex items-start gap-2 mt-3">
        <div>
          <xOptInput v-model:value="panel.price" v-model:error="errMsg.price" :is-button="false" :disabled="panel.isOrderMarket" :allow-input="(value) => onlyAllowNumber(value, future.scale)" placeholder="" class="flex-1" @focus="errMsg.triggerPrice = ''">
            <template #prefix>
              <span class="text-xs font-medium text-textTertiary">
                <template v-if="panel.isOrderMarket">{{ panel.isClosePosotion ? $t('最优价格平仓') : $t('最优价格开仓') }}</template>
                <template v-else>{{ $t('执行价格') }}</template>
              </span>
            </template>
          </xOptInput>
        </div>
        <x-select v-model="panel.stop.type" :list="stopExecutionPriceOpts" class="h-10 rounded-lg whitespace-nowrap bg-inputDefault" current-class="h-10 px-3" @update:model-value="panel.price = '';errMsg.price = ''">
          <template #current="data">
            {{ data.label }}
          </template>
        </x-select>
      </div>
    </div>
    <n-tooltip placement="top-end" trigger="focus">
      <template #default>
        <div class="text-xs">
          {{ minOrder.text }}
        </div>
      </template>
      <template #trigger>
        <div>
          <xOptInput
            v-if="!future.isGrants"
            ref="amountInput" v-model:error="errMsg.amount" :value="panel.sliderValue ? '' : panel.amount" :on-update:value="v => panel.amount = v" :is-button="false" :disabled="Boolean(panel.sliderValue)" :allow-input="amountAllowInput" placeholder="" class="mt-3 font-medium text-end" @click="onAmount" @focus="errMsg.amount = ''"
          >
            <template #prefix>
              <div class="max-w-[220px] leading-[1] text-start whitespace-normal">
                <span v-if="panel.sliderValue" class="text-xs font-medium text-textTertiary">{{ panel.isClosePosotion ? t('下单方向可平数量的 {0}', [`${panel.sliderValue}%`]) : t('下单方向可开数量的 {0}', [`${panel.sliderValue}%`]) }}</span>
                <span v-else class="text-xs font-medium text-textTertiary">{{ unitText }}</span>
              </div>
            </template>
            <template #suffix-extra>
              <button class="inline-flex items-center gap-1 text-xs font-medium ps-1 pe-3 text-textPrimary" @click="future.modal.unitSetting = true">
                <span>{{ unitName }}</span>
                <ArrowIcon class="w-2.5 h-1.5 ms-1" />
              </button>
            </template>
          </xOptInput>
          <div v-else>
            <div v-if="!bonus.grantsList.length || panel.pe === 2" class="flex items-center h-10 px-3 mt-2 text-sm font-medium rounded-full cursor-not-allowed bg-inputHovered text-textTertiary">
              {{ panel.pe === 2 ? t('bonus.closeAll') : t('bonus.noGrants') }}
            </div>
            <div v-else class="flex items-center justify-between h-10 px-3 mt-3 text-start bg-bg rounded-3xl" @click="bonus.modal.openGrantsList = true">
              <p class="text-textSecondary">
                {{ bonus.grantsInfo.quota ? t('grantsAccount') : t('bonus.selectGrants') }}
              </p>
              <button class="inline-flex items-center gap-1 text-white ps-1 pe-3">
                <span v-if="bonus.grantsInfo.quota">{{ bonus.grantsInfo.quota }} U</span>
                <span>{{ t('grantsAccount') }}</span>
                <ArrowIcon class="!w-2 !h-[6px]" />
              </button>
            </div>
            <div v-if="errMsg.amount" class="mt-2 text-xs text-start error-msg text-error">
              {{ errMsg.amount }}
            </div>
          </div>
        </div>
      </template>
    </n-tooltip>
    <XSlider v-model:value="panel.sliderValue" :disabled="future.isGrants" class="!mb-4 !mt-2" :marks="marks" :step="1" :min="0" :max="100" />
    <!-- tp/sl -->
    <TpSl v-show="panel.type !== 'TRIGGER'" ref="tpsl" class="pb-3 border-b border-border" :on-advance="onAdvance" />
    <!-- 开仓 -->
    <div class="mt-3">
      <div v-if="user.isLogin" class="grid grid-cols-2 gap-2 font-medium text-textOnBtn">
        <CommonXButton class="w-full" type="buy" @click="() => place(1)">
          {{ panel.isClosePosotion ? $t('平空') : $t('开多') }}
        </CommonXButton>
        <CommonXButton class="w-full" type="sell" @click="() => place(-1)">
          {{ panel.isClosePosotion ? $t('平多') : $t('开空') }}
        </CommonXButton>
      </div>
      <div v-else>
        <common-x-button class="w-full mt-4">
          <a class="flex items-center justify-center w-full h-full" :href="useBackUrl(localPath('/log-in'))">
            {{ t('登录') }}
          </a>
        </common-x-button>
        <common-x-button class="w-full mt-3" type="tertiary">
          <a class="flex items-center justify-center w-full h-full" :href="useBackUrl(localPath('/sign-up'))">
            {{ t('注册') }}
          </a>
        </common-x-button>
      </div>
      <div class="flex justify-between mt-3">
        <OrderInfer :side="1" />
        <OrderInfer :side="-1" class="text-end" />
      </div>
    </div>
    <FuturesModalUnitSetting v-model:show="future.modal.unitSetting" />
  </div>
</template>

<style lang="scss" scoped>

</style>
